import type { ReactNode } from 'react'
import type { JSX } from 'react'

import {
  Badge,
  ChannelAvatar,
  Flex,
  Link,
  Skeleton,
  Tag,
  Text,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import type { ContentItemCard_ContentItemData } from '~publish/components/ContentItemCard'
import { PostCard_Post } from '~publish/components/PostCard'
import { maskFragments } from '~publish/graphql'
import type { PostCard_PostData } from '~publish/components/PostCard'

import { BoardContentItemCard } from './BoardContentItemCard'
import { BoardPostCard } from './BoardPostCard'
import styles from './BoardColumn.module.css'

export type BoardColumnItem =
  | { kind: 'post'; id: string; post: PostCard_PostData }
  | { kind: 'contentItem'; id: string; item: ContentItemCard_ContentItemData }

type BoardColumnProps = {
  label: string
  count: number
  /** True while more pages remain unloaded, so `count` undercounts the real total. */
  countIsApproximate?: boolean
  /** Renders the label as a colored `Tag` pill instead of plain text, for Tag-grouped columns. */
  tagColor?: string
  /** Channel avatar, only set for Channel-grouped columns. */
  channelAvatar?: { service: ChannelType; src?: string | null }
  items: BoardColumnItem[]
  loading: boolean
  /** True while fetching an additional page onto an already-populated column. */
  fetchingMore: boolean
  /** A filter/sort change is in flight — appends a skeleton like `fetchingMore`, or shows one alone if nothing's displayable yet. */
  reloading: boolean
  error?: unknown
  lastPostId?: string
  lastElementRef: (node: HTMLElement | null) => void
  /** Set only on the column actually rendering the last item of the shared content-items connection. */
  lastContentItemId?: string
  contentItemLastElementRef: (node: HTMLElement | null) => void
  canShowMore: boolean
  /** Text preceding the "Show them" link — direction-specific (older vs. further out). */
  showMoreLabel: string
  onShowMore: () => void
}

function BoardColumnSkeleton(): JSX.Element {
  return (
    <Flex direction="column" gap="sm" className={styles.skeleton}>
      <Skeleton height={90} />
      <Skeleton height={90} />
      <Skeleton height={90} />
    </Flex>
  )
}

function BoardColumnErrorNotice(): JSX.Element {
  return (
    <Text color="critical" className={styles.empty}>
      Couldn&apos;t load this column.
    </Text>
  )
}

export function BoardColumn({
  label,
  count,
  countIsApproximate,
  tagColor,
  channelAvatar,
  items,
  loading,
  fetchingMore,
  reloading,
  error,
  lastPostId,
  lastElementRef,
  lastContentItemId,
  contentItemLastElementRef,
  canShowMore,
  showMoreLabel,
  onShowMore,
}: BoardColumnProps): JSX.Element {
  return (
    <div className={styles.column}>
      <Flex align="center" gap="xs" className={styles.header}>
        {channelAvatar && (
          <ChannelAvatar
            channel={channelAvatar.service}
            src={channelAvatar.src ?? undefined}
            alt=""
            size="small"
          />
        )}
        {tagColor ? (
          <Tag color={tagColor}>{label}</Tag>
        ) : (
          <Text weight="medium">{label}</Text>
        )}
        <Badge size="small">{countIsApproximate ? `${count}+` : count}</Badge>
      </Flex>
      <Flex as="ul" direction="column" gap="sm" className={styles.list}>
        {(loading || reloading) && items.length === 0 ? (
          <li>
            <BoardColumnSkeleton />
          </li>
        ) : error && items.length === 0 ? (
          <li>
            <BoardColumnErrorNotice />
          </li>
        ) : items.length === 0 ? (
          <li>
            <Text color="subtle" className={styles.empty}>
              Nothing here
            </Text>
          </li>
        ) : (
          <>
            {Boolean(error) && (
              <li>
                <BoardColumnErrorNotice />
              </li>
            )}
            {items.map((item): ReactNode => {
              const ref =
                item.kind === 'post' && item.id === lastPostId
                  ? lastElementRef
                  : item.kind === 'contentItem' && item.id === lastContentItemId
                    ? contentItemLastElementRef
                    : null

              return (
                <li key={`${item.kind}-${item.id}`} ref={ref}>
                  {item.kind === 'post' ? (
                    <BoardPostCard
                      post={maskFragments([PostCard_Post], item.post)}
                    />
                  ) : (
                    <BoardContentItemCard contentItem={item.item} />
                  )}
                </li>
              )
            })}
            {(fetchingMore || reloading) && (
              <li>
                <BoardColumnSkeleton />
              </li>
            )}
            {canShowMore && (
              <li>
                <Text color="subtle" size="sm">
                  {showMoreLabel} —{' '}
                  <Link as="button" type="button" onClick={onShowMore}>
                    Show them
                  </Link>
                </Text>
              </li>
            )}
          </>
        )}
      </Flex>
    </div>
  )
}
